import { useCallback, useEffect, useMemo, useState } from "react";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { ThreadAutoInteractionConfig } from "@features/Chat/chat-thread-auto-interaction-config";
import { useUIStore } from "@store/ui.store";

type Props = {
  threadAutoInteractionConfig?: ThreadAutoInteractionConfig | null;
  numberOfMessages: number;
  isSendingMessage: boolean;
  isReceivingMessageResponse: boolean;
};

export const useWelcomeChatThreadFlow = (props: Props) => {
  const {
    threadAutoInteractionConfig,
    numberOfMessages,
    isSendingMessage,
    isReceivingMessageResponse,
  } = props;
  const [isMessageBarOverlayHidden, setIsMessageBarOverlayHidden] =
    useState(true);
  const setMessageInputTextValue = useUIStore(
    (state) => state.setMessageInputTextValue
  );

  const messageForm = useUIStore((state) => state.messageForm);

  const nextMessageIndex = Math.ceil(numberOfMessages / 2) - 1;

  const hasMoreAutoInteractions = useMemo(
    () =>
      Boolean(
        threadAutoInteractionConfig &&
          nextMessageIndex >= 0 &&
          nextMessageIndex < threadAutoInteractionConfig.messages.length
      ),
    [nextMessageIndex, threadAutoInteractionConfig]
  );

  const handleFinishAutoTyping = useCallback(() => {
    if (
      !threadAutoInteractionConfig ||
      !threadAutoInteractionConfig.messages[nextMessageIndex]
    )
      return;

    setMessageInputTextValue(
      threadAutoInteractionConfig.messages[nextMessageIndex].text
    );

    setIsMessageBarOverlayHidden(true);

    setTimeout(() => {
      messageForm?.requestSubmit();
      setMessageInputTextValue("");
    }, 1000);

    appEventEmitter.emit({
      eventType: "sidekick-welcome-flow-complete",
    });
  }, [
    nextMessageIndex,
    threadAutoInteractionConfig,
    setMessageInputTextValue,
    messageForm,
  ]);

  useEffect(() => {
    if (
      isSendingMessage ||
      isReceivingMessageResponse ||
      !hasMoreAutoInteractions
    )
      return;

    setTimeout(() => {
      setIsMessageBarOverlayHidden(false);
    }, 3000);
  }, [hasMoreAutoInteractions, isReceivingMessageResponse, isSendingMessage]);

  return {
    handleFinishAutoTyping,
    isMessageBarOverlayHidden,
    nextMessageIndex,
    hasMoreAutoInteractions,
  };
};
